/**
 * Billing and record controls restored from the Flutter production console (parity audit
 * 2026-09-30). Every route here is SERVED:
 *  - the resident's CCM / BHI election and Part A covered stay (src/aithora/v3/billing-controls.ts),
 *  - the resident's payer and FHIR export / EHR push (src/aithora/router.ts),
 *  - the billing desk's claim status, biller and practitioner writes and the 837P / CSV
 *    downloads (src/aithora/reimbursement.ts under /api/financial/reimbursement, Aithora only).
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type { BillingPractitioner } from './finance-types';

const A = AITHORA_PREFIX;
const R = '/api/financial/reimbursement';

export type ElectionType = 'ccm_election' | 'bhi_election';
/** The five CMS notice items (MLN909188, Patient Consent), in reading order. */
export type ElectionDisclosure = 'service_availability' | 'cost_sharing' | 'one_biller_per_month' | 'right_to_stop' | 'explained_and_accepted';
export interface BillingElection {
  type: ElectionType;
  granted: boolean;
  witnessedBy: string | null;
  witnessAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  disclosures: Partial<Record<ElectionDisclosure, boolean>> | null;
  at: string | null;
  /** Granted, all five notice items, not revoked, not expired. */
  active: boolean;
}
export interface BillingElectionsResponse {
  residentId: string;
  elections: BillingElection[];
  required: ElectionDisclosure[];
  real: true;
}
export interface PartAStayResponse {
  residentId: string;
  onCoveredStay: boolean;
  updatedAt: string | null;
  real: true;
}
/** A claim's lifecycle states (reimbursement.ts CLAIM_TRANSITIONS). */
export type ClaimStatus = 'draft' | 'ready' | 'awaiting_signature' | 'submitted' | 'accepted' | 'denied' | 'paid';
/** The states a claim may move to from each state, as the server allows them. */
export const CLAIM_NEXT: Record<ClaimStatus, ClaimStatus[]> = {
  draft: ['ready', 'submitted'],
  ready: ['draft', 'submitted', 'awaiting_signature'],
  awaiting_signature: ['ready', 'submitted'],
  submitted: ['ready', 'accepted', 'denied', 'paid'],
  accepted: ['paid', 'denied'],
  denied: ['submitted'],
  paid: [],
};
export interface FhirResource {
  resourceType: string;
  [k: string]: unknown;
}
export interface EhrPushResult {
  sent: number;
  errors: string[];
}

/** GET /residents/:id/billing-elections: the latest CCM and BHI election and whether each is active. */
export const billingElections = route<BillingElectionsResponse>()('GET', `${A}/residents/:id/billing-elections`, { honest: true });
/** POST /residents/:id/billing-elections: record the resident's verbal election. All five notice items, confirm-first. */
export const billingElectionRecord = route<
  { ok: true; election: BillingElection; real: true },
  void,
  { consentType: ElectionType; disclosures: Record<ElectionDisclosure, boolean>; witnessedBy?: string; confirm: true }
>()('POST', `${A}/residents/:id/billing-elections`);
/** POST /residents/:id/billing-elections/revoke: the resident stopped. Confirm-first. */
export const billingElectionRevoke = route<{ ok: true; real: true }, void, { consentType: ElectionType; confirm: true }>()(
  'POST', `${A}/residents/:id/billing-elections/revoke`);

// ── Billing consent (spec 2026-10-14, src/aithora/v3/billing-consent-routes.ts) ──
export type ConsentMethod = 'e_signature_link' | 'tablet_in_person' | 'verbal_recorded_by_staff';
export type RepresentativeAuthority = 'guardian' | 'medical_poa' | 'healthcare_surrogate';
export interface ConsentSigner {
  kind: 'resident' | 'representative';
  name?: string;
  relationship?: string;
  authority?: RepresentativeAuthority;
  /** Where the authority is shown: the court order, the power of attorney, the surrogate note. */
  authorityEvidence?: string;
  /** The doctor certified in the record that the resident cannot decide. */
  incapacityCertified?: boolean;
  /** Surrogate only: no guardian or medical power of attorney agent exists. */
  noGuardianOrAgent?: boolean;
}
export interface BillingConsentView {
  id: string;
  type: 'ccm_election' | 'bhi_election' | 'apcm_election';
  granted: boolean;
  active: boolean;
  missingItems: ElectionDisclosure[];
  method: ConsentMethod | null;
  signer: { kind: string; name?: string | null; relationship?: string | null; authority?: string | null };
  practitionerId: string | null;
  confirmedBy: string | null;
  confirmedAt: string | null;
  withdrawnAt: string | null;
  at: string | null;
}
type ConsentWriteBody = { consentType: 'ccm_election' | 'bhi_election' | 'apcm_election'; practitionerId?: string; signer?: ConsentSigner; granted?: boolean };

/** GET /residents/:id/billing-consent: the latest consent per service, what it is missing, and the history. */
export const billingConsent = route<{ residentId: string; required: ElectionDisclosure[]; consents: BillingConsentView[]; history: BillingConsentView[]; real: true }>()(
  'GET', `${A}/residents/:id/billing-consent`, { honest: true });
/** POST /residents/:id/billing-consent/verbal: staff record a verbal yes or no, all five points explained. Atlas is refused. */
export const billingConsentVerbal = route<{ ok: true; id: string; method: ConsentMethod; at: string; real: true }, void,
  ConsentWriteBody & { explained: Record<ElectionDisclosure, boolean>; confirm: true }>()('POST', `${A}/residents/:id/billing-consent/verbal`);
/** POST /residents/:id/billing-consent/tablet: the resident or representative taps agree in person. Atlas is refused. */
export const billingConsentTablet = route<{ ok: true; id: string; method: ConsentMethod; at: string; real: true }, void,
  ConsentWriteBody & { acknowledged: Record<ElectionDisclosure, boolean>; agree: true; signatureText?: string }>()('POST', `${A}/residents/:id/billing-consent/tablet`);
/** POST /residents/:id/billing-consent/link: a one-time e-sign link by text or email (text consent gated). */
export const billingConsentLink = route<{ ok: true; linkId: string; url: string; expiresAt: string; sent: boolean; reason: string | null; real: true }, void,
  ConsentWriteBody & { channel: 'sms' | 'email'; to: string; language?: 'en' | 'es' }>()('POST', `${A}/residents/:id/billing-consent/link`);
/** POST /residents/:id/billing-consent/withdraw: the resident stopped; later claims for that practitioner are blocked. */
export const billingConsentWithdraw = route<{ ok: true; id: string; withdrawnAt: string; real: true }, void,
  { consentType: 'ccm_election' | 'bhi_election' | 'apcm_election'; practitionerId?: string; note?: string; requestedBy?: 'resident' | 'representative'; confirm: true }>()(
  'POST', `${A}/residents/:id/billing-consent/withdraw`);
/** GET /capture/consent-missing: residents a claim or this month's care is waiting on consent for, with the one-tap action. */
export const captureConsentMissing = route<{ facilityId: string | null; residents: Array<{ residentId: string; resident: string; facilityId: string; why: string; missingItems: ElectionDisclosure[]; lastLink: { channel: string; sentAt: string; to: string } | null; action: 'send_family_link' | 'record_consent' }>; real: true }, { facility?: string }>()(
  'GET', `${A}/capture/consent-missing`, { honest: true });
/** POST /capture/consent-missing/:residentId/send-link: one tap, a fresh link to the last representative contacted. */
export const captureConsentSendLink = route<{ ok: true; linkId: string; expiresAt: string; sent: boolean; reason: string | null; real: true }, void, { consentType?: string }>()(
  'POST', `${A}/capture/consent-missing/:residentId/send-link`);
/** POST /residents/:id/chronic-conditions: add an ICD-10-CM diagnosis to the problem list (facility admin or Aithora). */
export const residentDiagnosisAdd = route<{ ok: true; condition: { id: string; icd10: string; description: string | null; active: boolean }; real: true }, void, { icd10: string; description?: string }>()(
  'POST', `${A}/residents/:id/chronic-conditions`);
/** POST /residents/:id/billing-care-plan: the Revenue Integrity fix for a care plan gate (facility admin or Aithora). */
export const billingCarePlanRecord = route<
  { ok: true; carePlanId: string; comprehensiveness: { comprehensive: boolean; missing: string[] }; real: true },
  void,
  { problems: { icd10: string }[]; goals: { description: string }[]; interventions: { description: string }[]; title?: string }
>()('POST', `${A}/residents/:id/billing-care-plan`);
/** POST /residents/:id/care-minutes: documented minutes toward a code; 409 without an active election. */
export const careMinutesLog = route<{ ok: true; accrued: boolean; deduped: boolean; reason: string | null; real: true }, void, { cptCode: string; minutes: number }>()(
  'POST', `${A}/residents/:id/care-minutes`);
/** GET /residents/:id/part-a-stay */
export const partAStay = route<PartAStayResponse>()('GET', `${A}/residents/:id/part-a-stay`, { honest: true });
/** PUT /residents/:id/part-a-stay {active}: facility admin or Aithora only. */
export const partAStaySet = route<{ ok: true; residentId: string; onCoveredStay: boolean; real: true }, void, { active: boolean }>()(
  'PUT', `${A}/residents/:id/part-a-stay`);
/** PUT /residents/:id/payer {payerType}: facility admin or Aithora only; blank clears it. */
export const residentPayerSet = route<{ ok: true; payerType: string | null }, void, { payerType: string }>()('PUT', `${A}/residents/:id/payer`);

/** GET /fhir/patient/:userId: the resident as a US Core Patient. */
export const fhirPatient = route<FhirResource>()('GET', `${A}/fhir/patient/:userId`);
/** GET /fhir/observation/:userId: mood and cognition Observations, as a collection Bundle. */
export const fhirObservations = route<FhirResource>()('GET', `${A}/fhir/observation/:userId`);
/** GET /fhir/care-plan/:userId: Atlas care plan. */
export const fhirCarePlan = route<FhirResource>()('GET', `${A}/fhir/care-plan/:userId`);
/** POST /providers/push-to-ehr {resource}: send one resource to the building's connected EHR (gated server side). */
export const ehrPush = route<EhrPushResult, void, { resource: FhirResource; organizationId?: string }>()('POST', `${A}/providers/push-to-ehr`);

/** PATCH /api/financial/reimbursement/claims/status: move one claim on. Submitted waits for the practitioner's signature. */
export const claimStatusSet = route<
  { claim: { resident_id: string; cpt_code: string; period: string; status: ClaimStatus } },
  void,
  { residentId: string; cptCode: string; period: string; status: ClaimStatus; denialReason?: string }
>()('PATCH', `${R}/claims/status`);
/** PATCH /api/financial/reimbursement/practitioners/:id {active}: deactivate or reactivate a biller. */
export const practitionerSetActive = route<{ practitioner: BillingPractitioner }, void, { active: boolean }>()('PATCH', `${R}/practitioners/:id`);
/** POST /api/financial/reimbursement/residents/:id/practitioner: the resident's biller for CCM or BHI. */
export const residentBillerAssign = route<{ ok: true; resident: string; role: 'ccm' | 'bhi' }, void, { practitionerId: string; role: 'ccm' | 'bhi' }>()(
  'POST', `${R}/residents/:id/practitioner`);
/**
 * POST /api/financial/treasury/corporate: Aithora's own cash and monthly burn, in cents (operator
 * only), and optionally the runway model's five inputs (left out, the stored value stays).
 */
export const treasuryCorporateSet = route<unknown, void, {
  cashCents: number | null; monthlyBurnCents: number | null; asOf?: string; note?: string;
  opexMonthlyCents?: number | null; grossMargin?: number | null; boardMinimumCents?: number | null; collectionLagDays?: number | null; churnPct?: number | null;
}>()(
  'POST', '/api/financial/treasury/corporate');
/** PATCH /api/financial/prospects/:id {status}: move a prospect on the tracking board (operator only). */
export const intelProspectMove = route<{ prospect: { id: string; status: string } }, void, { status: 'tracked' | 'evaluating' | 'pursue' | 'pass' | 'contacted' | 'acquired' }>()(
  'PATCH', '/api/financial/prospects/:id');
/**
 * POST /api/financial/prospects {providerId, name, state}: put a catalog building on the
 * deal book (status tracked), so it can be sent to the Deal Desk from the ownership network.
 */
export const intelProspectCreate = route<{ prospect: { id: string; provider_id: string | null; name: string; status: string } }, void, { providerId: string; name: string; state?: string | null }>()(
  'POST', '/api/financial/prospects');
/** DELETE /api/financial/prospects/:id: take a prospect off the board (operator only). */
export const intelProspectRemove = route<{ ok: true }>()('DELETE', '/api/financial/prospects/:id');
/** The claims download path: ?format=x12 (837P EDI), 837p (837P columns) or csv, with organizationId and period. */
export const CLAIMS_EXPORT_PATH = `${R}/claims/export`;

export const billingControlsApi = {
  billingElections,
  billingElectionRecord,
  billingElectionRevoke,
  partAStay,
  partAStaySet,
  residentPayerSet,
  residentDiagnosisAdd,
  fhirPatient,
  fhirObservations,
  fhirCarePlan,
  ehrPush,
  claimStatusSet,
  practitionerSetActive,
  residentBillerAssign,
  treasuryCorporateSet,
  intelProspectMove,
  intelProspectRemove,
} as const;
